/**
 * GraphLegend — collapsible legend panel for the evidence graph.
 *
 * Shows only node types and edge types present in the current graph.
 * Anchored bottom-left, next to the ReactFlow Controls.
 */

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { NodeType, getNodeTypeColor, getNodeTypeLabel } from './types';

interface GraphLegendProps {
  /** Node types present in the current graph */
  nodeTypes: NodeType[];
  /** Whether the graph has internal (group) edges */
  hasInternalEdges?: boolean;
  /** Whether the graph has hierarchy edges */
  hasHierarchyEdges?: boolean;
  /** Whether the graph has conditional edges */
  hasConditionalEdges?: boolean;
}

export default function GraphLegend({
  nodeTypes,
  hasInternalEdges = false,
  hasHierarchyEdges = false,
  hasConditionalEdges = false,
}: GraphLegendProps) {
  const [isOpen, setIsOpen] = useState(true);

  // Deduplicate and sort node types for consistent display
  const uniqueTypes = useMemo(() => {
    const seen = new Set<NodeType>();
    return nodeTypes.filter((t) => {
      if (seen.has(t)) return false;
      seen.add(t);
      return true;
    });
  }, [nodeTypes]);

  const hasEdgeTypes = hasInternalEdges || hasHierarchyEdges || hasConditionalEdges;

  if (uniqueTypes.length === 0) return null;

  return (
    <div className="absolute bottom-4 left-14 z-10">
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2.5 py-1.5 bg-surface border border-border-default rounded-md shadow-bark-sm hover:bg-bg-warm text-xs font-medium text-fg-secondary transition-colors"
      >
        {isOpen ? <ChevronDown size={12} strokeWidth={1.5} /> : <ChevronRight size={12} strokeWidth={1.5} />}
        Legend
      </button>

      {/* Legend panel */}
      {isOpen && (
        <div className="mt-1 bg-surface border border-border-default rounded-lg shadow-bark-md p-3 min-w-[180px] max-w-[240px]">
          {/* Node types */}
          <div className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-1.5">
          Node types
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2">
            {uniqueTypes.map((type) => (
              <div key={type} className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getNodeTypeColor(type) }}
                />
                <span className="text-[11px] text-fg-secondary leading-tight truncate">
                  {getNodeTypeLabel(type)}
                </span>
              </div>
            ))}
          </div>

          {/* Edge types */}
          {hasEdgeTypes && (
            <>
              <div className="border-t border-divider my-2" />
              <div className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-1.5">
          Edge types
              </div>
              <div className="flex flex-col gap-1">
                {/* Direct edge always shown */}
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-0 border-t-2 border-border-strong flex-shrink-0" />
                  <span className="text-[11px] text-fg-secondary">Direct</span>
                </div>
                {hasInternalEdges && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-0 border-t-2 border-node-definition flex-shrink-0" />
                    <span className="text-[11px] text-fg-secondary">Internal</span>
                  </div>
                )}
                {hasConditionalEdges && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-0 border-t-2 border-warning flex-shrink-0" />
                    <span className="text-[11px] text-fg-secondary">Conditional</span>
                  </div>
                )}
                {hasHierarchyEdges && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-0 border-t-2 border-dashed border-border-default flex-shrink-0" />
                    <span className="text-[11px] text-fg-secondary">Hierarchy</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
